import { AnimatePresence, motion } from 'framer-motion'
import { ArrowRight, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import { cn } from '@/utils/cn'

interface ConnectionTargetBannerProps {
  visible: boolean
  sourceName: string
  sourceTypeLabel?: string
  onCancel: () => void
  /** What the next click will do. Defaults to the create-connection wording;
   *  other target-picking tools (e.g. Find Path) pass their own copy. */
  instruction?: ReactNode
  /** Tailwind position classes. Defaults to bottom-left inside the canvas
   *  wrapper, clear of the time-range filter strip and the bottom-right
   *  entity-count badge. */
  className?: string
}

/**
 * Floating instructional banner shown while the user is in "connection
 * mode" — i.e. after picking "Create Connection" from a node's ring menu
 * and before the target node has been clicked. Sits in the bottom-LEFT of
 * the graph canvas (the entity-count badge already lives bottom-right) and
 * self-handles the Escape key so the user always has a keyboard escape
 * hatch regardless of which surface holds focus.
 *
 * Visual: `bg-accent` surface — one palette step darker than the canvas
 * `--background` in light mode (and one step lighter in dark mode), so
 * the pill always reads as elevated against the graph without resorting
 * to a high-contrast reversal. The previous `bg-popover/95` rendered as
 * pure white over the warm off-white canvas and washed the instruction
 * text out.
 */
export function ConnectionTargetBanner({
  visible,
  sourceName,
  sourceTypeLabel,
  onCancel,
  instruction,
  className,
}: ConnectionTargetBannerProps) {
  useEffect(() => {
    if (!visible) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visible, onCancel])

  const dotColor = (sourceTypeLabel && LABEL_COLORS[sourceTypeLabel]?.fill) ?? 'hsl(var(--primary))'

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="connection-target-banner"
          role="status"
          aria-live="polite"
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 360, damping: 28, mass: 0.85 }}
          className={cn(
            'pointer-events-auto absolute bottom-6 left-6 z-30',
            'max-md:right-3 max-md:bottom-12 max-md:left-3',
            'flex items-center gap-3 rounded-xl border px-3 py-2',
            'border-border bg-accent text-accent-foreground',
            'shadow-[0_18px_48px_-16px_hsl(var(--foreground)/0.32),inset_0_1px_0_hsl(0_0%_100%/0.10)]',
            className
          )}
        >
          {/* Pulsing source-color dot — anchors the banner to the node */}
          <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
            <span
              className="absolute inset-0 animate-ping rounded-full opacity-60"
              style={{ backgroundColor: dotColor }}
            />
            <span
              className="relative h-2.5 w-2.5 rounded-full"
              style={{
                backgroundColor: dotColor,
                boxShadow: `0 0 0 2px hsl(var(--background)), 0 0 12px ${dotColor}88`,
              }}
            />
          </span>

          {/* Source identity — small type chip + name, vertically stacked */}
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="text-[9px] font-medium uppercase tracking-[0.18em] text-muted-foreground/80">
              {sourceTypeLabel || 'Source'}
            </span>
            <span
              className="max-w-[200px] truncate text-[12px] font-medium text-foreground"
              title={sourceName}
            >
              {sourceName}
            </span>
          </div>

          {/* Arrow + instruction */}
          <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/50" />
          <span className="text-[11px] text-muted-foreground max-md:min-w-0 max-md:flex-1">
            {instruction ?? (
              <>
                Click <span className="text-foreground/90 font-medium">target entity</span> to
                connect
              </>
            )}
          </span>

          {/* Cancel — both clickable and a keyboard hint. The kbd-style
              outline mirrors the in-app "press enter" / "press esc" cues
              used by the geofence draw panel. */}
          <button
            type="button"
            onClick={onCancel}
            className={cn(
              'ml-1 inline-flex items-center gap-1.5 rounded-md border px-1.5 py-1 max-md:px-2.5 max-md:py-2',
              'border-border/40 bg-muted/30 text-muted-foreground',
              'transition-colors hover:border-border/60 hover:bg-muted/60 hover:text-foreground'
            )}
            title="Cancel — Esc"
          >
            <X className="h-3 w-3" />
            <span className="font-mono text-[9px] uppercase tracking-[0.14em]">esc</span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
